import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogOut, ArrowLeft, FileText, Download, TrendingUp, TrendingDown, BarChart3, IndianRupee, ShoppingCart, Percent, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { invoiceAPI, returnAPI, discardAPI, productAPI, statsAPI } from '../services/api';

const Reports = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const username = localStorage.getItem('username') || 'User';
  const [activeTab, setActiveTab] = useState('analytics');
  const [showDialog, setShowDialog] = useState(false);
  const [reportType, setReportType] = useState('monthly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentReportName, setCurrentReportName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  
  const [invoices, setInvoices] = useState([]);
  const [returns, setReturns] = useState([]);
  const [discards, setDiscards] = useState([]);
  const [products, setProducts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  // Analytics date range
  const [analyticsPeriod, setAnalyticsPeriod] = useState('30');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [invoicesData, returnsData, discardsData, productsData, analyticsData] = await Promise.all([
        invoiceAPI.getAll(),
        returnAPI.getAll(),
        discardAPI.getAll(),
        productAPI.getAll(),
        statsAPI.getAnalytics()
      ]);
      setInvoices(invoicesData);
      setReturns(returnsData);
      setDiscards(discardsData);
      setProducts(productsData);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({ title: "Error", description: "Failed to load report data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadAnalytics = useCallback(async (days) => {
    try {
      const today = new Date();
      const start = new Date(today);
      start.setDate(today.getDate() - parseInt(days));
      const startStr = `${String(start.getDate()).padStart(2,'0')}/${String(start.getMonth()+1).padStart(2,'0')}/${start.getFullYear()}`;
      const endStr = `${String(today.getDate()).padStart(2,'0')}/${String(today.getMonth()+1).padStart(2,'0')}/${today.getFullYear()}`;
      const data = await statsAPI.getAnalytics(startStr, endStr);
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  }, []);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) { navigate('/'); return; }
    loadData();
  }, [navigate, loadData]);

  const handleAnalyticsPeriodChange = (val) => {
    setAnalyticsPeriod(val);
    loadAnalytics(val);
  };

  const handleLogout = () => { navigate('/dashboard?action=logout'); };

  const openReportDialog = (reportName) => {
    setCurrentReportName(reportName);
    setShowDialog(true);
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length === 3) return new Date(parts[2], parts[1] - 1, parts[0]);
    return new Date(dateStr);
  };

  const filterByDateRange = (data, dateField = 'date') => {
    const today = new Date();
    let filterStart, filterEnd;
    if (reportType === 'weekly') {
      filterStart = new Date(today); filterStart.setDate(today.getDate() - 7); filterEnd = today;
    } else if (reportType === 'monthly') {
      filterStart = new Date(today); filterStart.setMonth(today.getMonth() - 1); filterEnd = today;
    } else if (reportType === 'custom') {
      filterStart = new Date(startDate); filterEnd = new Date(endDate);
    }
    return data.filter(item => {
      const itemDate = parseDate(item[dateField]);
      return itemDate && itemDate >= filterStart && itemDate <= filterEnd;
    });
  };

  const generateBillingData = () => {
    const filteredInvoices = filterByDateRange(invoices);
    if (filteredInvoices.length === 0) return null;
    const billingData = [];
    filteredInvoices.forEach(invoice => {
      invoice.items?.forEach(item => {
        billingData.push({
          'Invoice No': invoice.invoice_no,
          'Date': invoice.date,
          'Time': invoice.time,
          'Product Name': item.name,
          'Brand': item.brand || '-',
          'Category': item.category || '-',
          'Barcode': item.barcode || '-',
          'Vendor': item.vendor_name || '-',
          'Quantity': item.quantity,
          'MRP': item.price?.toFixed(2),
          'Cost Price': item.cost_price?.toFixed(2) || (item.sales_price * 0.6).toFixed(2),
          'Sale Price': item.sales_price?.toFixed(2),
          'Item Total': item.item_total?.toFixed(2),
          'GST %': item.gst,
          'CGST': item.cgst?.toFixed(2),
          'SGST': item.sgst?.toFixed(2),
          'Discount': invoice.discount?.toFixed(2) || '0.00',
          'Payment Method': invoice.payment_method,
          'Final Payment': invoice.total?.toFixed(2),
          'User ID': invoice.created_by_username || invoice.created_by || '-'
        });
      });
    });
    return billingData;
  };

  const generateInvoiceSummaryData = () => {
    const filteredInvoices = filterByDateRange(invoices);
    if (filteredInvoices.length === 0) return null;
    return filteredInvoices.map(invoice => ({
      'Invoice No': invoice.invoice_no, 'Date': invoice.date, 'Time': invoice.time,
      'Total Items': invoice.items?.length || 0, 'Total Quantity': invoice.total_quantity,
      'Subtotal': invoice.subtotal?.toFixed(2), 'CGST': invoice.cgst?.toFixed(2),
      'SGST': invoice.sgst?.toFixed(2), 'Total GST': ((invoice.cgst || 0) + (invoice.sgst || 0)).toFixed(2),
      'Discount %': invoice.discount_percent?.toFixed(2) || '0', 'Discount Amount': invoice.discount?.toFixed(2) || '0.00',
      'Final Total': invoice.total?.toFixed(2), 'Savings': invoice.savings?.toFixed(2),
      'Payment Method': invoice.payment_method, 'User ID': invoice.created_by_username || invoice.created_by || '-'
    }));
  };

  const generateReturnData = () => {
    const filtered = filterByDateRange(returns);
    if (filtered.length === 0) return null;
    return filtered.map(ret => ({
      'Return ID': ret.id, 'Invoice No': ret.invoice_no, 'Date': ret.date,
      'Items': ret.items?.length || 0, 'Total Return Amount': ret.total_return_amount?.toFixed(2),
      'Processed By': ret.processed_by
    }));
  };

  const generateStockData = () => {
    if (products.length === 0) return null;
    return products.map(p => ({
      'Product Name': p.name, 'Brand': p.brand, 'Category': p.category,
      'Stock': p.stock, 'Unit': p.unit, 'Cost Price': p.cost_price?.toFixed(2),
      'Sales Price': p.sales_price?.toFixed(2), 'MRP': p.mrp?.toFixed(2) || p.price?.toFixed(2),
      'GST %': p.gst, 'HSN Code': p.hsn_code, 'Vendor': p.vendor_name || '-',
      'Expiry': p.expiry || 'N/A'
    }));
  };

  const generateDiscardData = () => {
    const filtered = filterByDateRange(discards);
    if (filtered.length === 0) return null;
    return filtered.map(d => ({
      'Discard ID': d.id, 'Date': d.date, 'Items': d.items?.length || 0,
      'Reason': d.reason, 'Processed By': d.processed_by
    }));
  };

  const generateGSTData = () => {
    const filteredInvoices = filterByDateRange(invoices);
    if (filteredInvoices.length === 0) return null;
    const gstSummary = {};
    filteredInvoices.forEach(invoice => {
      invoice.items?.forEach(item => {
        const gstRate = item.gst || 0;
        if (!gstSummary[gstRate]) gstSummary[gstRate] = { taxableAmount: 0, cgst: 0, sgst: 0, totalTax: 0, totalAmount: 0 };
        gstSummary[gstRate].taxableAmount += item.item_total - (item.cgst || 0) - (item.sgst || 0);
        gstSummary[gstRate].cgst += item.cgst || 0;
        gstSummary[gstRate].sgst += item.sgst || 0;
        gstSummary[gstRate].totalTax += (item.cgst || 0) + (item.sgst || 0);
        gstSummary[gstRate].totalAmount += item.item_total;
      });
    });
    return Object.entries(gstSummary).map(([rate, data]) => ({
      'GST Rate (%)': rate, 'Taxable Amount': data.taxableAmount.toFixed(2),
      'CGST': data.cgst.toFixed(2), 'SGST': data.sgst.toFixed(2),
      'Total Tax': data.totalTax.toFixed(2), 'Total Amount': data.totalAmount.toFixed(2)
    }));
  };

  const generateSalesSummaryData = () => {
    const filteredInvoices = filterByDateRange(invoices);
    if (filteredInvoices.length === 0) return null;
    
    // Payment summary with breakdown (including split payments distribution)
    const paymentSummary = { 
      Cash: { count: 0, amount: 0 }, 
      Card: { count: 0, amount: 0 }, 
      UPI: { count: 0, amount: 0 }, 
      Online: { count: 0, amount: 0 },
      Split: { count: 0, amount: 0 } 
    };
    
    let totalCost = 0, totalGST = 0, totalDiscount = 0, totalItems = 0;
    
    filteredInvoices.forEach(invoice => {
      const method = invoice.payment_method;
      
      if (method === 'Split' && invoice.payment_details) {
        // Distribute split payment amounts to respective modes
        paymentSummary.Split.count++;
        paymentSummary.Split.amount += invoice.total || 0;
        
        // Add split amounts to respective payment modes
        const cashAmount = parseFloat(invoice.payment_details.cash) || 0;
        const upiAmount = parseFloat(invoice.payment_details.upi) || 0;
        const cardAmount = parseFloat(invoice.payment_details.card) || 0;
        
        paymentSummary.Cash.amount += cashAmount;
        paymentSummary.UPI.amount += upiAmount;
        paymentSummary.Card.amount += cardAmount;
      } else if (method && method.startsWith('Online')) {
        // Online payments (Zomato/Swiggy)
        paymentSummary.Online.count++;
        paymentSummary.Online.amount += invoice.total || 0;
      } else if (paymentSummary[method]) {
        paymentSummary[method].count++;
        paymentSummary[method].amount += invoice.total || 0;
      }
      
      totalGST += (invoice.cgst || 0) + (invoice.sgst || 0);
      totalDiscount += invoice.discount || 0;
      invoice.items?.forEach(item => {
        const costPrice = item.cost_price || (item.sales_price * 0.6);
        totalCost += costPrice * (item.quantity || 1);
        totalItems += item.quantity || 1;
      });
    });
    
    const totalSales = filteredInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
    const totalInvoices = filteredInvoices.length;
    const avgInvoiceValue = totalSales / totalInvoices;
    const grossProfit = totalSales - totalCost - totalGST;
    const profitMargin = (grossProfit / totalSales * 100) || 0;
    
    return [
      { 'Metric': 'Total Revenue', 'Value': `${totalSales.toFixed(2)}` },
      { 'Metric': 'Total Cost', 'Value': `${totalCost.toFixed(2)}` },
      { 'Metric': 'Gross Profit', 'Value': `${grossProfit.toFixed(2)}` },
      { 'Metric': 'Profit Margin', 'Value': `${profitMargin.toFixed(2)}%` },
      { 'Metric': 'Total GST Collected', 'Value': `${totalGST.toFixed(2)}` },
      { 'Metric': 'Total Discount Given', 'Value': `${totalDiscount.toFixed(2)}` },
      { 'Metric': 'Total Orders', 'Value': totalInvoices },
      { 'Metric': 'Total Items Sold', 'Value': totalItems },
      { 'Metric': 'Average Order Value', 'Value': `${avgInvoiceValue.toFixed(2)}` },
      { 'Metric': 'Average Items per Order', 'Value': (totalItems / totalInvoices).toFixed(1) },
      { 'Metric': 'Cash Sales (incl. Split)', 'Value': `${paymentSummary.Cash.amount.toFixed(2)} (${paymentSummary.Cash.count} direct orders)` },
      { 'Metric': 'Card Sales (incl. Split)', 'Value': `${paymentSummary.Card.amount.toFixed(2)} (${paymentSummary.Card.count} direct orders)` },
      { 'Metric': 'UPI Sales (incl. Split)', 'Value': `${paymentSummary.UPI.amount.toFixed(2)} (${paymentSummary.UPI.count} direct orders)` },
      { 'Metric': 'Online Sales (Zomato/Swiggy)', 'Value': `${paymentSummary.Online.amount.toFixed(2)} (${paymentSummary.Online.count} orders)` },
      { 'Metric': 'Split Payment Orders', 'Value': `${paymentSummary.Split.amount.toFixed(2)} (${paymentSummary.Split.count} orders)` }
    ];
  };

  const downloadExcel = (data, filename) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
    const maxWidth = 20;
    const colWidths = Object.keys(data[0] || {}).map(key => ({
      wch: Math.min(maxWidth, Math.max(key.length, ...data.map(row => String(row[key] || '').length)))
    }));
    worksheet['!cols'] = colWidths;
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleGenerateReport = () => {
    setIsGenerating(true);
    setTimeout(() => {
      let data, filename;
      switch (currentReportName) {
        case 'Billing Report': data = generateBillingData(); filename = 'Billing_Report'; break;
        case 'Invoice Summary': data = generateInvoiceSummaryData(); filename = 'Invoice_Summary'; break;
        case 'Return Report': data = generateReturnData(); filename = 'Return_Report'; break;
        case 'Stock Report': data = generateStockData(); filename = 'Stock_Report'; break;
        case 'Discard Report': data = generateDiscardData(); filename = 'Discard_Report'; break;
        case 'GST Report': data = generateGSTData(); filename = 'GST_Report'; break;
        case 'Sales Summary': data = generateSalesSummaryData(); filename = 'Sales_Summary'; break;
        default: data = null;
      }
      if (!data) {
        toast({ title: "No Data", description: "No data available for the selected period", variant: "destructive" });
        setIsGenerating(false); return;
      }
      downloadExcel(data, filename);
      toast({ title: "Report Generated", description: `${currentReportName} downloaded successfully` });
      setIsGenerating(false); setShowDialog(false);
    }, 500);
  };

  const downloadFullAnalytics = () => {
    if (!analytics) return;
    const { summary, growth, payment_breakdown, daily_breakdown } = analytics;
    const rows = [
      { 'Metric': 'Period', 'Value': `${analytics.period?.start_date} to ${analytics.period?.end_date}` },
      { 'Metric': 'Days', 'Value': analytics.period?.days || 30 },
      { 'Metric': '', 'Value': '' },
      { 'Metric': 'Total Sales', 'Value': summary?.total_sales || 0 },
      { 'Metric': 'Total Cost', 'Value': summary?.total_cost || 0 },
      { 'Metric': 'Gross Profit', 'Value': summary?.gross_profit || 0 },
      { 'Metric': 'Profit Margin %', 'Value': summary?.profit_margin || 0 },
      { 'Metric': 'Total GST', 'Value': summary?.total_gst || 0 },
      { 'Metric': 'Total Discount', 'Value': summary?.total_discount || 0 },
      { 'Metric': 'Total Orders', 'Value': summary?.total_orders || 0 },
      { 'Metric': 'Avg Order Value', 'Value': summary?.avg_order_value || 0 },
      { 'Metric': '', 'Value': '' },
      { 'Metric': 'Sales Growth %', 'Value': growth?.sales_growth_percent || 0 },
      { 'Metric': 'Order Growth %', 'Value': growth?.order_growth_percent || 0 },
      { 'Metric': 'Prev Period Sales', 'Value': growth?.prev_period_sales || 0 },
      { 'Metric': 'Prev Period Orders', 'Value': growth?.prev_period_orders || 0 },
      { 'Metric': '', 'Value': '' },
      { 'Metric': 'Cash Sales', 'Value': payment_breakdown?.Cash?.amount || 0 },
      { 'Metric': 'Cash Orders', 'Value': payment_breakdown?.Cash?.count || 0 },
      { 'Metric': 'UPI Sales', 'Value': payment_breakdown?.UPI?.amount || 0 },
      { 'Metric': 'UPI Orders', 'Value': payment_breakdown?.UPI?.count || 0 },
      { 'Metric': 'Card Sales', 'Value': payment_breakdown?.Card?.amount || 0 },
      { 'Metric': 'Card Orders', 'Value': payment_breakdown?.Card?.count || 0 },
    ];
    if (daily_breakdown) {
      rows.push({ 'Metric': '', 'Value': '' });
      rows.push({ 'Metric': '--- Daily Breakdown ---', 'Value': '' });
      Object.entries(daily_breakdown).forEach(([date, data]) => {
        rows.push({ 'Metric': `${date} Sales`, 'Value': data.sales });
        rows.push({ 'Metric': `${date} Orders`, 'Value': data.orders });
        rows.push({ 'Metric': `${date} Profit`, 'Value': data.profit });
      });
    }
    downloadExcel(rows, 'Sales_Analytics');
    toast({ title: "Report Generated", description: "Sales Analytics downloaded" });
  };

  const reportDownloads = [
    { id: 'billing', name: 'Billing Report', description: 'Detailed billing records with product, GST, vendor info' },
    { id: 'invoice', name: 'Invoice Summary', description: 'Invoice-level summaries' },
    { id: 'return', name: 'Return Report', description: 'Product returns' },
    { id: 'stock', name: 'Stock Report', description: 'Current inventory status' },
    { id: 'discard', name: 'Discard Report', description: 'Discarded items' },
    { id: 'gst', name: 'GST Report', description: 'GST summary by rate' },
    { id: 'sales', name: 'Sales Summary', description: 'Sales with profit/loss analysis' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#e8ecf0' }}>
        <div className="text-lg text-gray-600">Loading reports...</div>
      </div>
    );
  }

  const s = analytics?.summary || {};
  const g = analytics?.growth || {};
  const pb = analytics?.payment_breakdown || {};
  const db = analytics?.daily_breakdown || {};

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#e8ecf0' }}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" className="flex items-center space-x-2" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <img src="https://customer-assets.emergentagent.com/job_6a116685-1875-4e0c-97f5-a9213e7bf53a/artifacts/isz5lodv_IMG_6110.PNG" alt="New Shop Logo" className="h-10 object-contain" />
            <span className="text-xl font-semibold text-gray-800">Reports</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-600">Welcome, <span className="font-semibold">{username}</span></span>
            <Button onClick={handleLogout} variant="outline" className="flex items-center space-x-2" data-testid="logout-btn">
              <LogOut className="w-4 h-4" /><span>Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 gap-2 mb-6 bg-white">
            <TabsTrigger value="analytics" className="text-sm font-medium" data-testid="analytics-tab">
              <BarChart3 className="w-4 h-4 mr-2" /> Sales Analytics
            </TabsTrigger>
            <TabsTrigger value="downloads" className="text-sm font-medium" data-testid="downloads-tab">
              <Download className="w-4 h-4 mr-2" /> Download Reports
            </TabsTrigger>
          </TabsList>

          {/* ===== ANALYTICS TAB ===== */}
          <TabsContent value="analytics">
            {/* Period Selector */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-800">Sales Analytics</h2>
              <div className="flex items-center space-x-2">
                <Select value={analyticsPeriod} onValueChange={handleAnalyticsPeriodChange}>
                  <SelectTrigger className="w-40 bg-white" data-testid="analytics-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 Days</SelectItem>
                    <SelectItem value="30">Last 30 Days</SelectItem>
                    <SelectItem value="90">Last 90 Days</SelectItem>
                    <SelectItem value="365">Last 1 Year</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={downloadFullAnalytics} variant="outline" size="sm" data-testid="download-analytics-btn">
                  <Download className="w-4 h-4 mr-1" /> Excel
                </Button>
              </div>
            </div>

            {/* Revenue & Profit Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="bg-white border-l-4 border-l-blue-500" data-testid="total-sales-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Revenue</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{Number(s.total_sales || 0).toLocaleString('en-IN', {style:'currency', currency:'INR'})}</p>
                    </div>
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                      <IndianRupee className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="mt-2 flex items-center text-xs">
                    {g.sales_growth_percent >= 0
                      ? <span className="text-green-600 flex items-center"><ArrowUpRight className="w-3 h-3 mr-0.5" />{g.sales_growth_percent?.toFixed(1)}%</span>
                      : <span className="text-red-600 flex items-center"><ArrowDownRight className="w-3 h-3 mr-0.5" />{Math.abs(g.sales_growth_percent || 0).toFixed(1)}%</span>
                    }
                    <span className="text-gray-400 ml-1">vs previous period</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-green-500" data-testid="gross-profit-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Gross Profit</p>
                      <p className={`text-2xl font-bold mt-1 ${(s.gross_profit || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {Number(s.gross_profit || 0).toLocaleString('en-IN', {style:'currency', currency:'INR'})}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                      {(s.gross_profit || 0) >= 0 ? <TrendingUp className="w-5 h-5 text-green-600" /> : <TrendingDown className="w-5 h-5 text-red-600" />}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Margin: <span className="font-semibold">{(s.profit_margin || 0).toFixed(1)}%</span></p>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-orange-500" data-testid="total-orders-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Orders</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{s.total_orders || 0}</p>
                    </div>
                    <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5 text-orange-600" />
                    </div>
                  </div>
                  <div className="mt-2 flex items-center text-xs">
                    {g.order_growth_percent >= 0
                      ? <span className="text-green-600 flex items-center"><ArrowUpRight className="w-3 h-3 mr-0.5" />{g.order_growth_percent?.toFixed(1)}%</span>
                      : <span className="text-red-600 flex items-center"><ArrowDownRight className="w-3 h-3 mr-0.5" />{Math.abs(g.order_growth_percent || 0).toFixed(1)}%</span>
                    }
                    <span className="text-gray-400 ml-1">vs previous period</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-purple-500" data-testid="avg-order-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Avg Order Value</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{Number(s.avg_order_value || 0).toLocaleString('en-IN', {style:'currency', currency:'INR'})}</p>
                    </div>
                    <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                      <Percent className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Discount given: {Number(s.total_discount || 0).toLocaleString('en-IN', {style:'currency', currency:'INR'})}</p>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Cost & Profit Breakdown */}
              <Card className="bg-white" data-testid="cost-profit-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold">Profit & Loss</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-gray-600">Total Revenue</span>
                      <span className="font-semibold text-gray-900">{Number(s.total_sales || 0).toLocaleString('en-IN', {style:'currency', currency:'INR'})}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-gray-600">Total Cost (COGS)</span>
                      <span className="font-semibold text-red-600">-{Number(s.total_cost || 0).toLocaleString('en-IN', {style:'currency', currency:'INR'})}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-gray-600">GST Collected</span>
                      <span className="font-semibold text-gray-700">{Number(s.total_gst || 0).toLocaleString('en-IN', {style:'currency', currency:'INR'})}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-sm text-gray-600">Discounts</span>
                      <span className="font-semibold text-orange-600">-{Number(s.total_discount || 0).toLocaleString('en-IN', {style:'currency', currency:'INR'})}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 bg-gray-50 px-3 rounded-lg">
                      <span className="text-sm font-semibold text-gray-800">Gross Profit</span>
                      <span className={`text-lg font-bold ${(s.gross_profit || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {Number(s.gross_profit || 0).toLocaleString('en-IN', {style:'currency', currency:'INR'})}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Breakdown */}
              <Card className="bg-white" data-testid="payment-breakdown-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold">Payment Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {['Cash', 'UPI', 'Card', 'Split'].map(method => {
                      const data = pb[method] || { count: 0, amount: 0 };
                      const pct = s.total_sales > 0 ? (data.amount / s.total_sales * 100) : 0;
                      const colors = { Cash: 'bg-green-500', UPI: 'bg-blue-500', Card: 'bg-purple-500', Split: 'bg-orange-500' };
                      return (
                        <div key={method} className="space-y-1">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">{method}</span>
                            <span className="font-medium">{Number(data.amount).toLocaleString('en-IN', {style:'currency', currency:'INR'})} ({data.count} orders)</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div className={`${colors[method]} h-2 rounded-full transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Growth Comparison */}
            <Card className="bg-white mb-6" data-testid="growth-comparison-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">Growth Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Current Sales</p>
                    <p className="text-lg font-bold text-gray-900">{Number(s.total_sales || 0).toLocaleString('en-IN', {style:'currency', currency:'INR'})}</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Previous Sales</p>
                    <p className="text-lg font-bold text-gray-600">{Number(g.prev_period_sales || 0).toLocaleString('en-IN', {style:'currency', currency:'INR'})}</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Sales Growth</p>
                    <p className={`text-lg font-bold ${(g.sales_growth_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(g.sales_growth_percent || 0).toFixed(1)}%
                    </p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Order Growth</p>
                    <p className={`text-lg font-bold ${(g.order_growth_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(g.order_growth_percent || 0).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Daily Breakdown Table */}
            {Object.keys(db).length > 0 && (
              <Card className="bg-white" data-testid="daily-breakdown-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold">Daily Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Date</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-600">Sales</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-600">Orders</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-600">Profit</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-600">Avg/Order</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(db).sort((a,b) => {
                          const da = a[0].split('/'); const dbb = b[0].split('/');
                          return new Date(dbb[2],dbb[1]-1,dbb[0]) - new Date(da[2],da[1]-1,da[0]);
                        }).map(([date, data]) => (
                          <tr key={date} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-medium">{date}</td>
                            <td className="py-2 px-3 text-right">{Number(data.sales).toLocaleString('en-IN', {style:'currency', currency:'INR'})}</td>
                            <td className="py-2 px-3 text-right">{data.orders}</td>
                            <td className={`py-2 px-3 text-right font-medium ${data.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {Number(data.profit).toLocaleString('en-IN', {style:'currency', currency:'INR'})}
                            </td>
                            <td className="py-2 px-3 text-right">{data.orders > 0 ? Number(data.sales / data.orders).toLocaleString('en-IN', {style:'currency', currency:'INR'}) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== DOWNLOADS TAB ===== */}
          <TabsContent value="downloads">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reportDownloads.map(report => (
                <Card key={report.id} className="bg-white hover:shadow-md transition-shadow cursor-pointer" data-testid={`report-${report.id}`}>
                  <CardContent className="p-6 text-center">
                    <FileText className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                    <h3 className="font-semibold text-gray-900 mb-1">{report.name}</h3>
                    <p className="text-xs text-gray-500 mb-4">{report.description}</p>
                    <Button
                      onClick={() => openReportDialog(report.name)}
                      className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold"
                      data-testid={`download-${report.id}-btn`}
                    >
                      <Download className="w-4 h-4 mr-2" /> Download
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
              <Card className="bg-white"><CardContent className="p-4 text-center"><p className="text-sm text-gray-600">Total Invoices</p><p className="text-2xl font-bold" data-testid="stat-invoices">{invoices.length}</p></CardContent></Card>
              <Card className="bg-white"><CardContent className="p-4 text-center"><p className="text-sm text-gray-600">Total Returns</p><p className="text-2xl font-bold" data-testid="stat-returns">{returns.length}</p></CardContent></Card>
              <Card className="bg-white"><CardContent className="p-4 text-center"><p className="text-sm text-gray-600">Products</p><p className="text-2xl font-bold" data-testid="stat-products">{products.length}</p></CardContent></Card>
              <Card className="bg-white"><CardContent className="p-4 text-center"><p className="text-sm text-gray-600">Total Sales</p><p className="text-2xl font-bold" data-testid="stat-total-sales">{Number(invoices.reduce((sum, inv) => sum + (inv.total || 0), 0)).toLocaleString('en-IN', {style:'currency', currency:'INR'})}</p></CardContent></Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Report Download Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{currentReportName}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Report Period</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger data-testid="report-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Last 7 Days</SelectItem>
                  <SelectItem value="monthly">Last 30 Days</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {reportType === 'custom' && (
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Start Date</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date-input" /></div>
                <div><Label>End Date</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="end-date-input" /></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={handleGenerateReport}
              disabled={isGenerating || (reportType === 'custom' && (!startDate || !endDate))}
              className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold"
              data-testid="generate-report-btn"
            >
              {isGenerating ? 'Generating...' : <><Download className="w-5 h-5 mr-2" /> Download Excel</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;
